import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API, useAuth } from '../context/AuthContext';
import { Heart, Plus, TrendingUp } from 'lucide-react';

export default function Journal() {
  const { token } = useAuth();
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({ total_entries: 0, mood_counts: {} });
  const [showAddForm, setShowAddForm] = useState(false);
  const [selectedMood, setSelectedMood] = useState('');
  const [note, setNote] = useState('');

  const moods = [
    { value: 'peaceful', label: 'Peaceful', emoji: '🙏', color: 'bg-blue-100 text-blue-700' },
    { value: 'happy', label: 'Happy', emoji: '😊', color: 'bg-yellow-100 text-yellow-700' },
    { value: 'calm', label: 'Calm', emoji: '🧘', color: 'bg-teal-100 text-teal-700' },
    { value: 'anxious', label: 'Anxious', emoji: '😟', color: 'bg-orange-100 text-orange-700' },
    { value: 'sad', label: 'Sad', emoji: '😢', color: 'bg-gray-100 text-gray-700' },
    { value: 'stressed', label: 'Stressed', emoji: '😫', color: 'bg-red-100 text-red-700' },
  ];

  useEffect(() => {
    loadEntries();
    loadStats();
  }, []);

  const loadEntries = async () => {
    try {
      const response = await axios.get(`${API}/mood-entries`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEntries(response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      console.error('Failed to load entries:', error);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/mood-entries/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedMood) return;

    try {
      await axios.post(
        `${API}/mood-entries`,
        { mood: selectedMood, note },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedMood('');
      setNote('');
      setShowAddForm(false);
      loadEntries();
      loadStats();
    } catch (error) {
      console.error('Failed to add entry:', error);
    }
  };

  const getMoodData = (moodValue) => {
    return moods.find(m => m.value === moodValue) || { emoji: '❤️', label: moodValue, color: 'bg-gray-100' };
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="journal-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 mb-2">Mood Journal</h1>
          <p className="text-gray-600">Track your emotional journey</p>
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-6 py-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-lg font-medium hover:from-purple-600 hover:to-blue-600 transition-all shadow-lg flex items-center space-x-2"
          data-testid="add-entry-button"
        >
          <Plus className="w-5 h-5" />
          <span>New Entry</span>
        </button>
      </div>

      {/* Add Entry Form */}
      {showAddForm && (
        <div className="bg-white rounded-xl p-6 shadow-lg border border-amber-100" data-testid="add-entry-form">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">How are you feeling?</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Select your mood</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {moods.map(mood => (
                  <button
                    key={mood.value}
                    type="button"
                    onClick={() => setSelectedMood(mood.value)}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      selectedMood === mood.value
                        ? 'border-purple-500 bg-purple-50 shadow-md'
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                    data-testid={`mood-${mood.value}`}
                  >
                    <div className="text-3xl mb-2">{mood.emoji}</div>
                    <div className="text-sm font-medium text-gray-700">{mood.label}</div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Note (optional)</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                rows="3"
                placeholder="What's on your mind?"
                data-testid="note-input"
              />
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={!selectedMood}
                className="flex-1 bg-gradient-to-r from-purple-500 to-blue-500 text-white py-3 rounded-lg font-medium hover:from-purple-600 hover:to-blue-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-entry-button"
              >
                Save Entry
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setSelectedMood('');
                  setNote('');
                }}
                className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Stats */}
      {stats.total_entries > 0 && (
        <div className="bg-white rounded-xl p-6 shadow-lg border border-amber-100">
          <div className="flex items-center space-x-2 mb-4">
            <TrendingUp className="w-6 h-6 text-purple-500" />
            <h2 className="text-xl font-semibold text-gray-800">Mood Statistics</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Object.entries(stats.mood_counts).map(([mood, count]) => {
              const moodData = getMoodData(mood);
              return (
                <div key={mood} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-2 mb-2">
                    <span className="text-2xl">{moodData.emoji}</span>
                    <span className="font-medium text-gray-800 capitalize">{mood}</span>
                  </div>
                  <div className="text-2xl font-bold text-purple-600">{count}</div>
                  <div className="text-sm text-gray-500">entries</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Entries List */}
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-800">Your Entries</h2>
        {entries.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-amber-100">
            <Heart className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No entries yet. Start tracking your mood!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {entries.map((entry) => {
              const moodData = getMoodData(entry.mood);
              return (
                <div
                  key={entry.id}
                  className="bg-white rounded-xl p-6 shadow-lg border border-amber-100 hover:shadow-xl transition-shadow"
                  data-testid={`entry-${entry.id}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-3xl">{moodData.emoji}</span>
                      <div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${moodData.color}`}>
                          {moodData.label}
                        </span>
                      </div>
                    </div>
                    <span className="text-sm text-gray-500">
                      {new Date(entry.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                  {entry.note && (
                    <p className="text-gray-700 bg-gray-50 p-4 rounded-lg">{entry.note}</p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
